import { useEffect, useRef, useState } from 'react';

type Props = {
  className?: string;
  selectClassName?: string;
  labelClassName?: string;
  label?: string;
  placeholder?: string;
  options: Array<Option>;
  name?: string;
  id?: string;
  register?: Function;
  onChange?: Function;
  setValue?: Function;
  validation?: object;
  error?: any;
  errorText?: string;
  defaultValue?: string;
  iconUrl?: string;
  prefix?: string;
  disabled?: boolean;
};

type Option = {
  code: string;
  name: string;
};

const CustomSelect = ({
  className,
  selectClassName = '',
  labelClassName = '',
  label,
  placeholder = '',
  options,
  id = '',
  name = '',
  register = () => { },
  onChange = () => { },
  setValue = () => { },
  validation,
  error = false,
  errorText = 'Please enter the value',
  defaultValue = '',
  iconUrl = '',
  prefix = '',
  disabled = false,
}: Props) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [value, setOptionValue] = useState(defaultValue);
  const [optionName, setOptionName] = useState<any>(
    options.find((option) => option.code === defaultValue)?.name
  );
  const [isErrorVisible, setIsErrorVisible] = useState(!!error);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {

    setIsErrorVisible(!!error);
  }, [error]);

  const handleChange = (selectedValue: string) => {

    if (isErrorVisible) {
      setIsErrorVisible(false);
    }
    onChange(selectedValue);
  };

  const onClose = (event: any) => {
    setShowDropdown(false);
  };

  useEffect(() => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase();
      setFilteredOptions(
        options.filter((option) =>
          option.name.toLowerCase().includes(lowerSearchValue)
        )
      );
    } else {
      setFilteredOptions(options);
    }
  }, [searchValue, options]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onClose && onClose(event);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  useEffect(() => {
    setOptionValue(defaultValue);
    setOptionName(
      options.find(
        (option) => option.code.toLowerCase() === defaultValue?.toLowerCase()
      )?.name
    );
  }, [defaultValue, options]);

  const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
    let parent = element?.parentElement ?? null;

    while (parent) {
      const { overflowY } = window.getComputedStyle(parent);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  };

  useEffect(() => {
    if (!showDropdown) {
      setOpenUpward(false);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (!wrapperRef.current || !dropdownRef.current) {
        return;
      }

      const triggerRect = wrapperRef.current.getBoundingClientRect();
      const dropdownHeight = Math.min(dropdownRef.current.scrollHeight, 410) + 12;
      const viewportHeight = window.innerHeight;
      const spaceBelowViewport = viewportHeight - triggerRect.bottom;
      const spaceAboveViewport = triggerRect.top;
      const shouldOpenUpward =
        spaceBelowViewport < dropdownHeight && spaceAboveViewport > spaceBelowViewport;

      setOpenUpward(shouldOpenUpward);

      const scrollParent = getScrollParent(wrapperRef.current);
      if (!scrollParent) {
        return;
      }

      const parentRect = scrollParent.getBoundingClientRect();

      if (shouldOpenUpward) {
        const dropdownTop = triggerRect.top - dropdownHeight;
        const overflowTop = parentRect.top - dropdownTop;

        if (overflowTop > 0) {
          scrollParent.scrollBy({
            top: -(overflowTop + 16),
            behavior: 'smooth',
          });
        }
      } else {
        const dropdownBottom = triggerRect.bottom + dropdownHeight;
        const overflowBottom = dropdownBottom - parentRect.bottom;

        if (overflowBottom > 0) {
          scrollParent.scrollBy({
            top: overflowBottom + 16,
            behavior: 'smooth',
          });
        }
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [showDropdown, filteredOptions.length]);

  const truncateOptionName = (name: string, maxLength: number) => {
    if (window.innerWidth <= 768 && name?.length > maxLength) {
      return name.substring(0, maxLength) + '...';
    }
    return name;
  };

  const displayOptionName = truncateOptionName(optionName, 25);

  return (
    <div className={`grid grid-cols-1 mb-6 ${className ? className : ''}`}>
      {label && (
        <label className={`text-xs text-[var(--text-muted)] mb-1 ${labelClassName}`}>
          {label}
        </label>
      )}
      {iconUrl && <img src={iconUrl} alt="start" className="absolute" />}
      <select
        id={id}
        name={name}
        className="hidden"
        {...register(name, validation)}
        onChange={(e) => handleChange(e.target.value)}
        value={value}
      >
        {placeholder && (
          <option value="" hidden>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.name}
          </option>
        ))}
      </select>
      <div className="relative" ref={wrapperRef}>
        <div
          className={`relative flex h-12 items-center justify-between rounded-lg border border-[var(--border-light)] bg-[var(--bg-surface)] px-4 text-base text-[var(--text-main)] shadow-sm transition-colors ${selectClassName} ${isErrorVisible ? 'border border-danger' : ''
            } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
          onClick={() => !disabled && setShowDropdown(true)}
        >
          <div className="flex-1 truncate">
            {optionName ? (
              `${prefix}${displayOptionName}`
            ) : (
              <span className="text-[var(--text-muted)]">{placeholder}</span>
            )}
          </div>

          {!disabled && optionName && (
            <button
              type="button"
              className="ms-2 cursor-pointer border-none bg-transparent outline-none"
              onClick={(e) => {
                e.stopPropagation(); // prevent the dropdown from opening/closing
                setValue(name, '');
                setOptionValue('');
                handleChange('');
                setOptionName('');
              }}
            >
              <img
                src="/assets/icons/cross.svg"
                alt="close"
                className={`w-5 h-5 cursor-pointer`}
              ></img>
            </button>
          )}

          <span className="ms-2 flex flex-shrink-0 items-center align-middle">
            <img
              src="/assets/icons/dropdown_arrow.svg"
              alt="arrow"
              className={`theme-icon h-4 w-4 opacity-70 transition-transform ${!showDropdown ? 'rotate-180' : ''}`}
            />
          </span>
        </div>
        {showDropdown && (
          <div
            className={`absolute z-10 max-h-[410px] w-full overflow-scroll rounded-lg bg-[var(--bg-surface)] p-3 text-[var(--text-main)] custom-scroll ${openUpward ? 'bottom-full mb-2' : 'mt-2'
              }`}
            ref={dropdownRef}
            style={{
              boxShadow: 'var(--shadow-card)',
              border: '1px solid var(--border-light)',
            }}
          >
            {(label === 'Country' ||
              label === 'Shipper' ||
              label === 'Transporter' ||
              label === 'License issuing authority' ||
              label === 'Vehicle Make' ||
              label === 'Nationality') && (
                <input
                  className="mb-2 h-12 w-full rounded-lg border border-[var(--border-light)] bg-[var(--bg-surface)] px-4 text-base text-[var(--text-main)] shadow-sm outline-none placeholder:text-[var(--text-muted)]"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              )}
            {filteredOptions.map((option) => (
              <div
                key={option.code}
                className="cursor-pointer rounded-lg px-3 py-2.5 transition-colors hover:bg-[var(--color-primary)] hover:text-[var(--text-invert)]"
                onClick={() => {
                  if (!disabled) {
                    setValue(name, option.code);
                    setOptionValue(option.code);
                    handleChange(option.code);
                    setOptionName(option.name);
                    setShowDropdown(false);
                    setSearchValue('');
                  }
                }}
              >
                {option.name}
              </div>
            ))}
          </div>
        )}
      </div>
      {isErrorVisible && (
        <p className="mt-1 text-xs text-[var(--color-error)]">{errorText}</p>
      )}
    </div>
  );
};

export default CustomSelect;
